import {I18nKeys} from '@multipass-i18n';
import {Button, Notice, Panel} from '@multipass-ui-components';
import {ScriptPreferences} from '@multipass-user-service-dto';
import {syncModelWithSelectorOptions, usePublishEvent, useSyncModelWithSelector} from 'esp-js-react';
import {AlertTriangle, ArrowUpRight, CreditCard, FileText, Heart, Info, Languages, Lock, LogOut, LucideIcon, Trash2, User} from 'lucide-react';
import * as React from 'react';

import {AppVersion} from '../../app-config';
import {LOCALE_META, SESSION_STORE_ID, SessionEvents, SessionStoreModel, useCurrentLocale, useT, useTReason} from '../../logic';
import {iconFor} from '../legal/legalDocumentDisplay';

interface DefRowProps {
    label: string;
    value: string;
    locked?: boolean;
}

const DefRow: React.FunctionComponent<DefRowProps> = ({label, value, locked}: DefRowProps) => (
    <div className="flex items-center justify-between gap-4 border-b border-border-subtle py-3 last:border-b-0">
        <dt className="text-label font-semibold text-text-secondary">{label}</dt>
        <dd className={`flex items-center gap-2 text-right text-body ${locked ? 'text-text-muted' : 'text-text-primary'}`}>
            {value}
            {locked && <Lock size={16} className="text-text-muted"/>}
        </dd>
    </div>
);

const StatusRow: React.FunctionComponent<{label: string; status: string}> = ({label, status}: {label: string; status: string}) => (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <span className="text-label font-semibold text-text-secondary">{label}</span>
        <span className="inline-flex items-center gap-2 rounded-pill border border-border-subtle bg-canvas px-3.5 py-[5px] text-label font-semibold text-text-primary">
            <span className="h-2 w-2 rounded-full bg-text-muted"/>
            {status}
        </span>
    </div>
);

const DocLinkRow: React.FunctionComponent<{type: string; label: string; icon: LucideIcon}> = ({type, label, icon: Icon}: {type: string; label: string; icon: LucideIcon}) => (
    <a href={`/legal#${type}`} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 border-b border-border-subtle py-3 no-underline last:border-b-0">
        <Icon size={20} className="flex-none text-text-secondary"/>
        <span className="flex-1 text-body font-medium text-text-primary group-hover:text-brand-emphasis">{label}</span>
        <ArrowUpRight size={20} className="flex-none text-text-muted group-hover:text-text-secondary"/>
    </a>
);

const DeleteAccountPanel: React.FunctionComponent<{deleteError: string | null; deleteErrorCode: string | null}> = ({deleteError, deleteErrorCode}: {deleteError: string | null; deleteErrorCode: string | null}) => {
    const publishEvent = usePublishEvent();
    const t = useT();
    const tReason = useTReason();
    const [confirming, setConfirming] = React.useState(false);
    const onConfirmDelete = () => publishEvent(SESSION_STORE_ID, SessionEvents.deleteAccountRequested, {});
    return (
        <Panel icon={Trash2} title={t(I18nKeys.account.deleteAccount)} danger className="md:col-span-2">
            <p className="mb-5 text-label text-text-secondary">
                {t(I18nKeys.account.deleteBody)}
            </p>
            {deleteError && <Notice role="alert" className="mb-5">{tReason(deleteErrorCode, deleteError)}</Notice>}
            {confirming ? (
                <div className="rounded-md border border-warning bg-warning-bg p-4">
                    <p className="mb-4 flex items-start gap-2 text-label text-text-primary">
                        <AlertTriangle size={16} className="mt-0.5 flex-none text-warning"/>
                        {t(I18nKeys.account.deleteConfirmWarning)}
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Button type="button" variant="secondary" onClick={() => setConfirming(false)}>{t(I18nKeys.common.cancel)}</Button>
                        <Button type="button" variant="danger" onClick={onConfirmDelete}>
                            <Trash2 size={20}/>
                            {t(I18nKeys.account.deleteConfirm)}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="danger" onClick={() => setConfirming(true)}>
                        <Trash2 size={20}/>
                        {t(I18nKeys.account.deleteAccount)}
                    </Button>
                </div>
            )}
        </Panel>
    );
};

export const AccountPage: React.FunctionComponent = () => {
    const publishEvent = usePublishEvent();
    const t = useT();
    const currentLocale = useCurrentLocale();
    const session = useSyncModelWithSelector<SessionStoreModel, SessionStoreModel>(
        m => m,
        syncModelWithSelectorOptions<SessionStoreModel>().setStoreId(SESSION_STORE_ID)
    );
    const displayName = session.displayName ?? '';
    const email = session.email ?? '';
    const scriptPreference = session.scriptPreference;
    const onSignOut = () => publishEvent(SESSION_STORE_ID, SessionEvents.signOut, {});
    return (
        <section className="px-5 pb-16 pt-12">
            <div className="mx-auto max-w-[60rem]">
                <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <h1 className="mb-1 text-headline">{t(I18nKeys.account.title)}</h1>
                        <p className="flex items-center gap-2 text-body text-text-secondary">
                            {t(I18nKeys.common.signedInAs)} <span className="font-semibold text-text-primary">{displayName}</span>
                        </p>
                    </div>
                    <Button type="button" variant="secondary" onClick={onSignOut}>
                        <LogOut size={20}/>
                        <span className="max-[440px]:hidden">{t(I18nKeys.account.signOut)}</span>
                    </Button>
                </div>
                <div className="grid items-start gap-5 md:grid-cols-2">
                    <Panel icon={User} title={t(I18nKeys.account.profile)}>
                        <dl>
                            <DefRow label={t(I18nKeys.account.emailLabel)} value={email}/>
                            <DefRow label={t(I18nKeys.account.displayNameLabel)} value={displayName} locked/>
                            <DefRow
                                label={t(I18nKeys.account.scriptPreferenceLabel)}
                                value={scriptPreference === ScriptPreferences.Simplified ? t(I18nKeys.account.scriptSimplified) : t(I18nKeys.account.scriptTraditional)}
                                locked
                            />
                            <DefRow label={t(I18nKeys.account.localeLabel)} value={t(LOCALE_META[currentLocale].nameKey)}/>
                        </dl>
                        <p className="mt-4 flex items-center gap-2 text-caption text-text-muted">
                            <Languages size={16} className="flex-none"/>
                            {t(I18nKeys.account.scriptChangeInApp, {appName: t(I18nKeys.common.appName)})}
                        </p>
                        <p className="mt-2 flex items-center gap-2 text-caption text-text-muted">
                            <Lock size={16} className="flex-none"/>
                            {t(I18nKeys.account.profileLockNote)}
                        </p>
                    </Panel>
                    <Panel icon={CreditCard} title={t(I18nKeys.account.subscription)} comingSoon comingSoonLabel={t(I18nKeys.common.comingSoon)}>
                        <StatusRow label={t(I18nKeys.account.currentPlan)} status={t(I18nKeys.account.freePlan)}/>
                        <p className="mb-5 text-label text-text-secondary">
                            {t(I18nKeys.account.subscriptionBody)}
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Button type="button" disabled aria-disabled>{t(I18nKeys.account.upgradePlan)}</Button>
                        </div>
                    </Panel>
                    <Panel icon={Heart} title={t(I18nKeys.account.patreon)} comingSoon comingSoonLabel={t(I18nKeys.common.comingSoon)}>
                        <StatusRow label={t(I18nKeys.account.membership)} status={t(I18nKeys.account.notLinked)}/>
                        <p className="mb-5 text-label text-text-secondary">
                            {t(I18nKeys.account.patreonBody)}
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Button type="button" variant="secondary" disabled aria-disabled>{t(I18nKeys.account.linkPatreon)}</Button>
                            <Button type="button" variant="secondary" disabled aria-disabled>{t(I18nKeys.account.manageMembership)}</Button>
                        </div>
                    </Panel>
                    <Panel icon={FileText} title={t(I18nKeys.account.termsPrivacy)} className="md:col-span-2">
                        <DocLinkRow type="terms" label={t(I18nKeys.common.termsOfService)} icon={iconFor('terms')}/>
                        <DocLinkRow type="privacy" label={t(I18nKeys.common.privacyPolicy)} icon={iconFor('privacy')}/>
                    </Panel>
                    <Panel icon={Info} title={t(I18nKeys.account.about)} className="md:col-span-2">
                        <div className="flex items-center gap-3 py-3">
                            <Info size={20} className="flex-none text-text-secondary"/>
                            <span className="flex-1 text-body font-medium text-text-primary">{t(I18nKeys.account.version)}</span>
                            <span className="flex-none text-body font-semibold tabular-nums text-text-muted">{AppVersion.display}</span>
                        </div>
                    </Panel>
                    <DeleteAccountPanel deleteError={session.deleteError} deleteErrorCode={session.deleteErrorCode}/>
                </div>
            </div>
        </section>
    );
};
